import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import DetectionOverlay from './DetectionOverlay';
import ColorLegend from './ColorLegend';
import './VideoUpload.css';

const API_URL = 'https://cv.ainnect.me';

const VideoUpload = ({ selectedModel }) => {
    const [videoSrc, setVideoSrc] = useState(null);
    const [detections, setDetections] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [currentFrameDetections, setCurrentFrameDetections] = useState([]);
    const [videoMetaDimensions, setVideoMetaDimensions] = useState({ width: 0, height: 0 });
    const [displayDimensions, setDisplayDimensions] = useState({ width: 0, height: 0 });

    const fileInputRef = useRef(null);
    const videoRef = useRef(null);

    const computeDisplayDimensions = useCallback(() => {
        if (!videoRef.current) return;

        const naturalWidth = videoRef.current.videoWidth || videoMetaDimensions.width;
        const naturalHeight = videoRef.current.videoHeight || videoMetaDimensions.height;

        if (!naturalWidth || !naturalHeight) {
            setDisplayDimensions({
                width: videoRef.current.clientWidth || 0,
                height: videoRef.current.clientHeight || 0,
            });
            return;
        }

        const ratio = naturalWidth / naturalHeight;
        const maxViewportHeight = Math.max(window.innerHeight * 0.7, 320);
        const maxHeight = Math.min(maxViewportHeight, 720);
        const maxWidth = Math.min(window.innerWidth - 48, 900);

        let width = maxWidth;
        let height = width / ratio;

        if (height > maxHeight) {
            height = maxHeight;
            width = height * ratio;
        }

        const minWidth = 280;
        if (width < minWidth) {
            width = minWidth;
            height = width / ratio;
        }

        setDisplayDimensions({
            width: width,
            height: height,
        });
    }, [videoMetaDimensions]);

    useEffect(() => {
        computeDisplayDimensions();
        window.addEventListener('resize', computeDisplayDimensions);
        return () => window.removeEventListener('resize', computeDisplayDimensions);
    }, [computeDisplayDimensions, videoSrc]);

    const handleLoadedMetadata = () => {
        if (!videoRef.current) return;
        setVideoMetaDimensions({
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight,
        });
        computeDisplayDimensions();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setVideoSrc(url);
            setDetections(null);
            setError(null);
            setCurrentFrameDetections([]);
            setVideoMetaDimensions({ width: 0, height: 0 });
            setDisplayDimensions({ width: 0, height: 0 });
        }
    };

    const handleDetect = async () => {
        if (!fileInputRef.current.files[0]) return;

        setIsProcessing(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('video', fileInputRef.current.files[0]);

            const response = await fetch(`${API_URL}/detect-video?model=${selectedModel || 'best_v8s'}&frame_skip=3`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }

            const result = await response.json();
            setDetections(result);
        } catch (err) {
            console.error('Detection error:', err);
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const processedFrames = useMemo(() => {
        if (!detections?.frames || !detections?.video_info?.fps) return [];
        const fps = detections.video_info.fps || 30;
        return detections.frames
            .map((frame) => ({
                ...frame,
                time: frame.frame_number / fps,
            }))
            .sort((a, b) => a.time - b.time);
    }, [detections]);

    const legendDetections = useMemo(() => {
        if (!processedFrames.length) return [];
        return processedFrames.flatMap((frame) => frame.boxes || []);
    }, [processedFrames]);

    const updateDetectionsForTime = useCallback(
        (mediaTime) => {
            if (!processedFrames.length) {
                setCurrentFrameDetections([]);
                return;
            }

            let closestFrame = processedFrames[0];
            let minDelta = Math.abs(mediaTime - closestFrame.time);

            for (let i = 1; i < processedFrames.length; i++) {
                const frame = processedFrames[i];
                const delta = Math.abs(mediaTime - frame.time);

                if (delta < minDelta) {
                    closestFrame = frame;
                    minDelta = delta;
                } else if (frame.time > mediaTime && delta > minDelta) {
                    break;
                }
            }

            const fps = detections?.video_info?.fps || 30;
            const threshold = Math.max(0.12, 2 / fps);
            if (minDelta <= threshold) {
                setCurrentFrameDetections(closestFrame.boxes || []);
            } else {
                setCurrentFrameDetections([]);
            }
        },
        [processedFrames, detections?.video_info?.fps]
    );

    // Sync detections with video playback
    useEffect(() => {
        if (!videoRef.current || !processedFrames.length) return;

        const video = videoRef.current;
        let frameCallbackId = null;

        if (typeof video.requestVideoFrameCallback === 'function') {
            const onFrame = (_now, metadata) => {
                const time = metadata?.mediaTime ?? video.currentTime;
                updateDetectionsForTime(time);
                frameCallbackId = video.requestVideoFrameCallback(onFrame);
            };
            frameCallbackId = video.requestVideoFrameCallback(onFrame);

            return () => {
                if (typeof video.cancelVideoFrameCallback === 'function' && frameCallbackId !== null) {
                    video.cancelVideoFrameCallback(frameCallbackId);
                }
            };
        }

        const fallbackUpdate = () => updateDetectionsForTime(video.currentTime);
        video.addEventListener('timeupdate', fallbackUpdate);
        return () => video.removeEventListener('timeupdate', fallbackUpdate);
    }, [processedFrames, updateDetectionsForTime]);

    return (
        <div className="video-upload-container">
            <div className="upload-controls">
                <input
                    type="file"
                    accept="video/*"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    className="file-input"
                />
                <button
                    onClick={handleDetect}
                    disabled={!videoSrc || isProcessing}
                    className="detect-btn"
                >
                    {isProcessing ? 'Đang xử lý video...' : 'Phát hiện Video'}
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {videoSrc && (
                <div className="video-preview-wrapper">
                <div
                    className="video-stage"
                    style={
                        displayDimensions.width
                            ? {
                                  width: `${displayDimensions.width}px`,
                                  height: `${displayDimensions.height}px`,
                              }
                            : undefined
                    }
                >
                    <video
                        ref={videoRef}
                        src={videoSrc}
                        controls
                        onLoadedMetadata={handleLoadedMetadata}
                        className="video-preview"
                        style={
                            displayDimensions.width
                                ? {
                                      width: `${displayDimensions.width}px`,
                                      height: `${displayDimensions.height}px`,
                                  }
                                : undefined
                        }
                    />
                    {detections && (
                        <DetectionOverlay
                            detections={currentFrameDetections}
                            imageWidth={detections.video_info.width}
                            imageHeight={detections.video_info.height}
                            videoWidth={displayDimensions.width || videoMetaDimensions.width || detections.video_info.width}
                            videoHeight={displayDimensions.height || videoMetaDimensions.height || detections.video_info.height}
                        />
                    )}
                </div>
                </div>
            )}

            {detections && (
                <div className="video-stats">
                    <p>FPS: {detections.video_info.fps}</p>
                    <p>Total Frames: {detections.video_info.total_frames}</p>
                    <p>Processed Frames: {detections.video_info.processed_frames}</p>
                    <p>Total Inference Time: {Math.round(detections.total_inference_ms)}ms</p>
                </div>
            )}

            {legendDetections.length > 0 && (
                <ColorLegend detections={legendDetections} />
            )}
        </div>
    );
};

export default VideoUpload;
